/**
 * WordPress dependencies
 */
import { useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { Button } from '@wordpress/components';

/**
 * Booking Filters component — compact inline filter bar.
 *
 * @param {Object}   props
 * @param {Object}   props.filters        Current filter values.
 * @param {Function} props.onFilterChange Callback when filters change.
 * @param {Function} props.onRefresh      Callback to refresh the list.
 * @return {JSX.Element} The BookingFilters component.
 */
const BookingFilters = ({ filters, onFilterChange, onRefresh }) => {
    const [localFilters, setLocalFilters] = useState(filters);
    const [isExpanded, setIsExpanded] = useState(false);

    /**
     * Handle filter input change
     *
     * @param {string} field The field name.
     * @param {*}      value The new value.
     */
    const handleFilterChange = (field, value) => {
        const newFilters = { ...localFilters, [field]: value };
        setLocalFilters(newFilters);
    };

    /**
     * Apply filters
     */
    const applyFilters = () => {
        onFilterChange(localFilters);
    };

    /**
     * Handle Enter key on search input
     *
     * @param {KeyboardEvent} e The keyboard event.
     */
    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            applyFilters();
        }
    };

    /**
     * Clear all filters
     */
    const clearFilters = () => {
        const clearedFilters = {
            status: '',
            search: '',
            event_id: 0,
            date_from: '',
            date_to: ''
        };
        setLocalFilters(clearedFilters);
        onFilterChange(clearedFilters);
    };

    /**
     * Check if any filters are active
     *
     * @return {boolean} True if any filters are active.
     */
    const hasActiveFilters = () => {
        return Object.values(localFilters).some(value =>
            value !== '' && value !== 0 && value !== null && value !== undefined
        );
    };

    /**
     * Count active filters
     *
     * @return {number} Number of active filters.
     */
    const activeFilterCount = () => {
        return Object.values(localFilters).filter(value =>
            value !== '' && value !== 0 && value !== null && value !== undefined
        ).length;
    };

    const statusOptions = [
        { label: __('All Statuses', 'nemtly-booking'), value: '' },
        { label: __('Pending', 'nemtly-booking'), value: 'pending' },
        { label: __('Confirmed', 'nemtly-booking'), value: 'confirmed' },
        { label: __('Cancelled', 'nemtly-booking'), value: 'cancelled' },
        { label: __('Waitlisted', 'nemtly-booking'), value: 'waitlisted' }
    ];

    return (
        <div className="nb-bg-white nb-rounded-lg nb-shadow-sm nb-border nb-border-slate-200 nb-mb-4">
            {/* Compact filter bar — always visible */}
            <div className="nb-px-4 nb-py-3">
                <div className="nb-flex nb-items-center nb-gap-3 nb-flex-wrap">
                    {/* Search input */}
                    <div className="nb-flex-1 nb-min-w-[200px] nb-max-w-sm">
                        <input
                            type="text"
                            value={localFilters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            placeholder={__('Search bookings...', 'nemtly-booking')}
                            className="nb-block nb-w-full nb-px-3 nb-py-1.5 nb-border nb-border-slate-300 nb-rounded-md nb-text-sm focus:nb-ring-1 focus:nb-ring-slate-500 focus:nb-border-slate-500"
                        />
                    </div>

                    {/* Status dropdown */}
                    <select
                        value={localFilters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        className="nb-px-3 nb-py-1.5 nb-border nb-border-slate-300 nb-rounded-md nb-text-sm focus:nb-ring-1 focus:nb-ring-slate-500 focus:nb-border-slate-500"
                    >
                        {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>

                    {/* More filters toggle */}
                    <button
                        type="button"
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="nb-inline-flex nb-items-center nb-gap-1 nb-px-3 nb-py-1.5 nb-text-sm nb-text-slate-600 hover:nb-text-slate-900 nb-border nb-border-slate-300 nb-rounded-md hover:nb-bg-slate-50 nb-transition-colors nb-cursor-pointer"
                    >
                        <svg className="nb-w-4 nb-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                        {isExpanded ? __('Less', 'nemtly-booking') : __('More', 'nemtly-booking')}
                        {!isExpanded && activeFilterCount() > 0 && (
                            <span className="nb-inline-flex nb-items-center nb-justify-center nb-w-5 nb-h-5 nb-text-xs nb-font-medium nb-bg-slate-600 nb-text-white nb-rounded-full">
                                {activeFilterCount()}
                            </span>
                        )}
                    </button>

                    {/* Apply button */}
                    <Button
                        onClick={applyFilters}
                        className="nb-bg-slate-600 hover:nb-bg-slate-700 nb-text-white nb-px-3 nb-py-1.5 nb-rounded-md nb-text-sm nb-font-medium nb-transition-colors"
                    >
                        {__('Filter', 'nemtly-booking')}
                    </Button>

                    {/* Clear button */}
                    {hasActiveFilters() && (
                        <button
                            type="button"
                            onClick={clearFilters}
                            className="nb-text-sm nb-text-slate-500 hover:nb-text-slate-700 nb-underline nb-cursor-pointer"
                        >
                            {__('Clear', 'nemtly-booking')}
                        </button>
                    )}
                </div>
            </div>

            {/* Expanded date filters — toggled */}
            {isExpanded && (
                <div className="nb-px-4 nb-py-3 nb-border-t nb-border-slate-100 nb-bg-slate-50">
                    <div className="nb-flex nb-items-center nb-gap-4 nb-flex-wrap">
                        <label className="nb-flex nb-items-center nb-gap-2 nb-text-sm nb-text-slate-600">
                            {__('From', 'nemtly-booking')}
                            <input
                                type="date"
                                value={localFilters.date_from}
                                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                                className="nb-px-2 nb-py-1.5 nb-border nb-border-slate-300 nb-rounded-md nb-text-sm focus:nb-ring-1 focus:nb-ring-slate-500 focus:nb-border-slate-500"
                            />
                        </label>
                        <label className="nb-flex nb-items-center nb-gap-2 nb-text-sm nb-text-slate-600">
                            {__('To', 'nemtly-booking')}
                            <input
                                type="date"
                                value={localFilters.date_to}
                                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                                className="nb-px-2 nb-py-1.5 nb-border nb-border-slate-300 nb-rounded-md nb-text-sm focus:nb-ring-1 focus:nb-ring-slate-500 focus:nb-border-slate-500"
                            />
                        </label>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingFilters;
